// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Audit event action types for Sprint 1.0.0 + 1.1.0.
 * See: docs/compliance/audit-event-taxonomy.md
 */
export const AuditActionType = {
    // Case Lifecycle
    CASE_CREATED: 'case_created',
    CASE_UPDATED: 'case_updated',
    STATUS_CHANGED: 'status_changed',
    SUBSTATUS_CHANGED: 'substatus_changed',
    ASSIGNMENT_CHANGED: 'assignment_changed',
    CASE_CLOSED: 'case_closed',
    CASE_REJECTED: 'case_rejected',
    // Evidence
    NOTE_ADDED: 'note_added',
    ATTACHMENT_UPLOADED: 'attachment_uploaded',
    ATTACHMENT_DOWNLOADED: 'attachment_downloaded',
    // SLA
    SLA_PAUSED: 'sla_paused',
    SLA_RESUMED: 'sla_resumed',
    // Admin Config
    NOTIFICATION_SETTINGS_UPDATED: 'notification_settings_updated',
    // Auth (emitted by auth module)
    LOGIN_SUCCESS: 'login_success',
    LOGIN_FAILURE: 'login_failure',
    LOGOUT: 'logout',
    SESSION_EXPIRED: 'session_expired',
    // Sprint 1.1.0 additions
    ATTACHMENT_DELETED: 'attachment_deleted',
    REFERENCE_ADDED: 'reference_added',
    CHECKLIST_UPDATED: 'checklist_updated',
    IDENTITY_VERIFIED: 'identity_verified',
    CLOSURE_SUMMARY_ADDED: 'closure_summary_added',
    APPROVAL_GRANTED: 'approval_granted',
    TEMPLATE_APPLIED: 'template_applied',
    EXPORT_GENERATED: 'export_generated',
    SLA_WARNING: 'sla_warning',
    SLA_OVERDUE: 'sla_overdue',
    SLA_ESCALATION_CHANGED: 'sla_escalation_changed',
    SLA_BREACHED: 'sla_breached',
    TEMPLATE_CREATED: 'template_created',
    TEMPLATE_UPDATED: 'template_updated',
    TEMPLATE_DELETED: 'template_deleted',
    // Fulfillment (Sprint #1216)
    FULFILLMENT_ASSIGNED: 'fulfillment_assigned',
    FULFILLMENT_TASK_UPDATED: 'fulfillment_task_updated',
    FULFILLMENT_SIGNOFF: 'fulfillment_signoff',
    FULFILLMENT_TASK_DELETED: 'fulfillment_task_deleted',
    RESPONSE_GENERATED: 'response_generated',
    RESPONSE_SENT: 'response_sent',
    EXTENSION_REQUESTED: 'extension_requested',
    EXTENSION_DECISION: 'extension_decision',
    // Sprint 43.1.0 E9 — Final audit taxonomy gaps
    // Emitted by Sprint 43.1.0 batch 2 (E2 ResponseDelivery) when a
    // rendered response is dispatched via email/portal/postal/in-person.
    RESPONSE_DELIVERED: 'response_delivered',
    // Emitted by Sprint 43.1.0 batch 1 (E8 CrossBorderDetector) when a
    // DSR triggers the Decree 13 / PDPL Art 25 cross-border dossier flow.
    CROSS_BORDER_DOSSIER_TRIGGERED: 'cross_border_dossier_triggered',
    // Sprint 43.2.0 batch 1 — Admin & Retention & Breach events
    //
    // Response-template events REUSE the existing TEMPLATE_CREATED /
    // TEMPLATE_UPDATED / TEMPLATE_DELETED constants above (1.1.0). The E1
    // admin surface emits TEMPLATE_DELETED with `metadata.deactivated =
    // true` to preserve soft-delete semantics without adding a duplicate
    // event type. No new template_* entries are added here.
    //
    // Department admin (E2) — new first-class events since the 1.1.0
    // family did not cover department lifecycle or membership.
    DEPARTMENT_CREATED: 'department_created',
    DEPARTMENT_RENAMED: 'department_renamed',
    DEPARTMENT_DEACTIVATED: 'department_deactivated',
    DEPARTMENT_MEMBER_ADDED: 'department_member_added',
    DEPARTMENT_MEMBER_REMOVED: 'department_member_removed',
    // Retention policy (E4) — CRUD on `grc_retention_policies` plus
    // per-attachment stamping on identity-proof uploads.
    RETENTION_POLICY_CREATED: 'retention_policy_created',
    RETENTION_POLICY_UPDATED: 'retention_policy_updated',
    RETENTION_APPLIED: 'retention_applied',
    // Breach trigger (E5) — operator flags a DSR case as breach-causing,
    // which creates a `grc_data_breaches` record and starts the 72h Mẫu
    // số 08 filing clock via CrossBorderFilingService.
    DATA_BREACH_DETECTED: 'data_breach_detected',
    MPS_FILING_STARTED: 'mps_filing_started',
    // ── Sprint 47.5.0 — Forseti AI Legal Advisor (foundation) ─────────
    // Family plan §3.3 + §17. Codes registered here at foundation; emission
    // begins in 47.5.1+ as each module is implemented.
    // AI Advisor chat surface (47.5.4)
    AI_ADVISOR_CHAT_CREATED: 'ai_advisor_chat_created',
    AI_ADVISOR_CHAT_RENAMED: 'ai_advisor_chat_renamed',
    AI_ADVISOR_CHAT_DELETED: 'ai_advisor_chat_deleted',
    AI_ADVISOR_MESSAGE_SENT: 'ai_advisor_message_sent',
    AI_ADVISOR_MESSAGE_STREAMED: 'ai_advisor_message_streamed',
    AI_ADVISOR_TOOL_CALLED: 'ai_advisor_tool_called',
    AI_ADVISOR_TOOL_CONFIRMATION_REQUESTED: 'ai_advisor_tool_confirmation_requested',
    AI_ADVISOR_TOOL_CONFIRMATION_GRANTED: 'ai_advisor_tool_confirmation_granted',
    AI_ADVISOR_TOOL_CONFIRMATION_DENIED: 'ai_advisor_tool_confirmation_denied',
    AI_ADVISOR_PERSONA_SWITCHED: 'ai_advisor_persona_switched',
    // Sprint 48.0.0b — sub-agent orchestration. `_suggested` fires when
    // the persona-router surfaces a target persona; `_accepted` fires when
    // the user clicks through and a new chat thread is opened with the
    // handoff state seeded.
    AI_ADVISOR_HANDOFF_SUGGESTED: 'ai_advisor_handoff_suggested',
    AI_ADVISOR_HANDOFF_ACCEPTED: 'ai_advisor_handoff_accepted',
    // Sprint 48.0.0c — memory browse / curate. `_promoted` fires when an
    // admin locks a memory against retention GC; `_gc_swept` fires once
    // per nightly retention sweep with the count metadata.
    AI_ADVISOR_MEMORY_UPDATED: 'ai_advisor_memory_updated',
    AI_ADVISOR_MEMORY_DELETED: 'ai_advisor_memory_deleted',
    AI_ADVISOR_MEMORY_PROMOTED: 'ai_advisor_memory_promoted',
    AI_ADVISOR_MEMORY_GC_SWEPT: 'ai_advisor_memory_gc_swept',
    AI_ADVISOR_PROVIDER_SWITCHED: 'ai_advisor_provider_switched',
    AI_ADVISOR_RATE_LIMIT_EXCEEDED: 'ai_advisor_rate_limit_exceeded',
    AI_ADVISOR_PROMPT_INJECTION_BLOCKED: 'ai_advisor_prompt_injection_blocked',
    AI_ADVISOR_TASK_UPDATED: 'ai_advisor_task_updated',
    AI_ADVISOR_SEARCH_REGULATORY: 'ai_advisor_search_regulatory',
    AI_ADVISOR_CITATION_UNVERIFIED: 'ai_advisor_citation_unverified',
    AI_ADVISOR_REGULATORY_PACKS_INDEXED: 'ai_advisor_regulatory_packs_indexed',
    AI_ADVISOR_REGULATORY_PACK_ADDED: 'ai_advisor_regulatory_pack_added',
    // AI Advisor cross-module loop (47.5.8)
    AI_ADVISOR_EVIDENCE_STATUS_CHANGED: 'ai_advisor_evidence_status_changed',
    AI_ADVISOR_LINEAGE_VIEWED: 'ai_advisor_lineage_viewed',
    AI_ADVISOR_LINEAGE_EXPORTED: 'ai_advisor_lineage_exported',
    AI_ADVISOR_TASK_CONVERTED_TO_WORKFLOW_RUN: 'ai_advisor_task_converted_to_workflow_run',
    AI_ADVISOR_RECORD_SUMMARY_FETCHED: 'ai_advisor_record_summary_fetched',
    // Cross-module AI workflow exemplar (47.9.6) — pattern adopted by all
    // 48.1.x + 48.2.x cross-module workflows. Three events per workflow run:
    // start → produced → accepted/rejected/partially_accepted. Per
    // docs/engineering/forseti-cross-module-workflows.md §3 "audit trail".
    AI_ADVISOR_WORKFLOW_STARTED: 'ai_advisor_workflow_started',
    AI_ADVISOR_ASSISTED_DRAFT_PRODUCED: 'ai_advisor_assisted_draft_produced',
    AI_ADVISOR_DRAFT_ACCEPTED: 'ai_advisor_draft_accepted',
    AI_ADVISOR_DRAFT_REJECTED: 'ai_advisor_draft_rejected',
    AI_ADVISOR_DRAFT_PARTIALLY_ACCEPTED: 'ai_advisor_draft_partially_accepted',
    // AI Workflows (47.5.5)
    AI_WORKFLOW_CREATED: 'ai_workflow_created',
    AI_WORKFLOW_UPDATED: 'ai_workflow_updated',
    AI_WORKFLOW_DELETED: 'ai_workflow_deleted',
    AI_WORKFLOW_SHARED: 'ai_workflow_shared',
    AI_WORKFLOW_HIDDEN: 'ai_workflow_hidden',
    AI_WORKFLOW_RUN_STARTED: 'ai_workflow_run_started',
    AI_WORKFLOW_RUN_COMPLETED: 'ai_workflow_run_completed',
    AI_WORKFLOW_RUN_FAILED: 'ai_workflow_run_failed',
    AI_WORKFLOW_RUN_CANCELLED: 'ai_workflow_run_cancelled',
    AI_WORKFLOW_GENERATED_ATTACHMENT: 'ai_workflow_generated_attachment',
    AI_WORKFLOW_GENERATED_TASK: 'ai_workflow_generated_task',
    AI_WORKFLOW_RELOAD_BUILTIN: 'ai_workflow_reload_builtin',
    // AI Tabular Reviews (47.5.6)
    AI_TABULAR_REVIEW_CREATED: 'ai_tabular_review_created',
    AI_TABULAR_REVIEW_COLUMN_ADDED: 'ai_tabular_review_column_added',
    AI_TABULAR_REVIEW_COLUMN_REMOVED: 'ai_tabular_review_column_removed',
    AI_TABULAR_REVIEW_CELL_EXTRACTED: 'ai_tabular_review_cell_extracted',
    AI_TABULAR_REVIEW_CELL_FAILED: 'ai_tabular_review_cell_failed',
    AI_TABULAR_REVIEW_RUN_STARTED: 'ai_tabular_review_run_started',
    AI_TABULAR_REVIEW_RUN_COMPLETED: 'ai_tabular_review_run_completed',
    AI_TABULAR_REVIEW_RUN_CANCELLED: 'ai_tabular_review_run_cancelled',
    AI_TABULAR_REVIEW_APPLIED_WORKFLOW: 'ai_tabular_review_applied_workflow',
    // AI Document Review / Redlines (47.5.3)
    AI_DOCUMENT_EDIT_PROPOSED: 'ai_document_edit_proposed',
    AI_DOCUMENT_EDIT_ACCEPTED: 'ai_document_edit_accepted',
    AI_DOCUMENT_EDIT_REJECTED: 'ai_document_edit_rejected',
    AI_DOCUMENT_VERSION_PROMOTED: 'ai_document_version_promoted',
    // Provider + token billing (47.5.1)
    AI_PROVIDER_API_KEY_SET: 'ai_provider_api_key_set',
    AI_PROVIDER_API_KEY_ROTATED: 'ai_provider_api_key_rotated',
    AI_PROVIDER_API_KEY_CLEARED: 'ai_provider_api_key_cleared',
    AI_PROVIDER_HEALTH_CHECKED: 'ai_provider_health_checked',
    AI_PROVIDER_MODELS_DISCOVERED: 'ai_provider_models_discovered',
    AI_USAGE_LEDGER_WRITTEN: 'ai_usage_ledger_written',
    AI_QUOTA_EXHAUSTED: 'ai_quota_exhausted',
    AI_QUOTA_OVERRIDE_GRANTED: 'ai_quota_override_granted',
    AI_QUOTA_TOPPED_UP: 'ai_quota_topped_up',
    AI_QUOTA_RECONCILIATION_DRIFT_ALERT: 'ai_quota_reconciliation_drift_alert',
    // Self-service "Buy More" billing (48.5.x)
    AI_BILLING_INTENT_CREATED: 'ai_billing_intent_created',
    AI_BILLING_UNITS_PURCHASED: 'ai_billing_units_purchased',
    // AI Projects (47.5.2)
    AI_PROJECT_CREATED: 'ai_project_created',
    AI_PROJECT_RENAMED: 'ai_project_renamed',
    AI_PROJECT_DELETED: 'ai_project_deleted',
    AI_PROJECT_MEMBER_ADDED: 'ai_project_member_added',
    AI_PROJECT_MEMBER_REMOVED: 'ai_project_member_removed',
    AI_PROJECT_MEMBER_ROLE_CHANGED: 'ai_project_member_role_changed',
    AI_PROJECT_FOLDER_CREATED: 'ai_project_folder_created',
    AI_PROJECT_FOLDER_RENAMED: 'ai_project_folder_renamed',
    AI_PROJECT_FOLDER_DELETED: 'ai_project_folder_deleted',
    AI_PROJECT_DOCUMENT_ATTACHED: 'ai_project_document_attached',
    AI_PROJECT_DOCUMENT_UNATTACHED: 'ai_project_document_unattached',
    // AI evidence status transitions (per family plan §17.2 — 47.5.5 / 47.5.8)
    EVIDENCE_DRAFT_CREATED_BY_AI: 'evidence_draft_created_by_ai',
    EVIDENCE_MOVED_TO_REVIEW: 'evidence_moved_to_review',
    EVIDENCE_APPROVED: 'evidence_approved',
    EVIDENCE_REJECTED: 'evidence_rejected',
    // Sprint 58.0.0 (Phase 25 / Sprint A) — PDPL Evidence Pack assembly
    // lifecycle. Emitted by PdplEvidencePackZipService. Each event carries
    // pack_id + pack_sha256 + pack_type + dossier_id + org_slug + an
    // assembled_via flag ('deterministic' or 'ai_assisted', the latter
    // when Sprint G's Forseti workflow supplied ai_assistance). Send-side
    // lifecycle events (manual proof / direct send / authority response)
    // land in Sprints C and D.
    PDPL_EVIDENCE_PACK_ASSEMBLY_STARTED: 'pdpl_evidence_pack_assembly_started',
    PDPL_EVIDENCE_PACK_FILENAME_VALIDATION_PASSED: 'pdpl_evidence_pack_filename_validation_passed',
    PDPL_EVIDENCE_PACK_FILENAME_VALIDATION_FAILED: 'pdpl_evidence_pack_filename_validation_failed',
    PDPL_EVIDENCE_PACK_MANIFEST_GENERATED: 'pdpl_evidence_pack_manifest_generated',
    PDPL_EVIDENCE_PACK_ZIP_CREATED: 'pdpl_evidence_pack_zip_created',
    PDPL_EVIDENCE_PACK_ASSEMBLY_FAILED: 'pdpl_evidence_pack_assembly_failed',
    // Sprint 58.1.0 (Phase 25 / Sprint B) — PDPL email-draft + manual
    // sent-proof lifecycle. `draft_finalized` is reserved for Sprint D's
    // direct-send wiring so D doesn't have to touch the shared audit
    // taxonomy again. Each event carries pack_id + draft_id + version +
    // locale + pack_sha256. `sent_proof_uploaded` additionally carries
    // proof_id + envelope.attachment_sha256_at_send so chain-of-custody
    // queries can match the proof to the assembled pack.
    PDPL_EMAIL_DRAFT_CREATED: 'pdpl_email_draft_created',
    PDPL_EMAIL_DRAFT_EDITED: 'pdpl_email_draft_edited',
    PDPL_EMAIL_DRAFT_FINALIZED: 'pdpl_email_draft_finalized',
    PDPL_EMAIL_SENT_PROOF_UPLOADED: 'pdpl_email_sent_proof_uploaded',
    // Sprint 58.2.0 (Phase 25 / Sprint C) — PDPL email-provider config
    // lifecycle. Per-tenant customer-controlled outbound mail config.
    // Architecture lock: AesirX MUST NOT be the default sender — these
    // events record the customer's own provider standing up + consent
    // chain-of-custody (verbatim disclaimer text sha256 in the consent
    // event payload). Connection-check events carry latency_ms +
    // sanitised failure_reason; the actual configured password is never
    // emitted into the audit feed.
    PDPL_EMAIL_PROVIDER_CREATED: 'pdpl_email_provider_created',
    PDPL_EMAIL_PROVIDER_CONSENT_ACKNOWLEDGED: 'pdpl_email_provider_consent_acknowledged',
    PDPL_EMAIL_PROVIDER_CONNECTION_CHECK_SUCCEEDED: 'pdpl_email_provider_connection_check_succeeded',
    PDPL_EMAIL_PROVIDER_CONNECTION_CHECK_FAILED: 'pdpl_email_provider_connection_check_failed',
    PDPL_EMAIL_PROVIDER_DISABLED: 'pdpl_email_provider_disabled',
    // Sprint 58.3.0 (Phase 25 / Sprint D) — Direct-send lifecycle. Each
    // event carries org_id + send_id + pack_id + provider_config_id.
    // Send events additionally carry latency_ms + message_id (on success)
    // or sanitised failure_reason (on failure). Authority response events
    // carry classification + (on reclassification) previous + reason.
    // Hard-copy events carry courier + tracking_number.
    PDPL_EMAIL_SEND_ATTEMPTED: 'pdpl_email_send_attempted',
    PDPL_EMAIL_SEND_SUCCEEDED: 'pdpl_email_send_succeeded',
    PDPL_EMAIL_SEND_FAILED: 'pdpl_email_send_failed',
    PDPL_AUTHORITY_RESPONSE_RECORDED: 'pdpl_authority_response_recorded',
    PDPL_AUTHORITY_RESPONSE_RECLASSIFIED: 'pdpl_authority_response_reclassified',
    PDPL_HARD_COPY_FOLLOWUP_RECORDED: 'pdpl_hard_copy_followup_recorded',
    PDPL_HARD_COPY_FOLLOWUP_UPDATED: 'pdpl_hard_copy_followup_updated',
};
/**
 * Audit event actor types.
 */
export const ActorType = {
    USER: 'user',
    SYSTEM: 'system',
};
/**
 * Well-known system actor IDs.
 * See: docs/compliance/audit-event-taxonomy.md - Section 4.3
 */
export const SystemActorId = {
    INTAKE: 'system:intake',
    SLA_ENGINE: 'system:sla-engine',
    EXPORT_JOB: 'system:export-job',
    CMP_GATEWAY: 'system:cmp-gateway',
    NOTIFICATION: 'system:notification',
};
/**
 * Event sensitivity levels.
 * See: docs/compliance/audit-event-taxonomy.md - Section 5
 */
export const EventSensitivity = {
    PUBLIC: 'public',
    INTERNAL: 'internal',
    SENSITIVE: 'sensitive',
    RESTRICTED: 'restricted',
};
// ── Sprint 43.0.0 — audit payload enrichments (E6) ────────────────
//
// Runtime-value mirrors of the type unions exported from
// `types/case.ts` (`PauseReason`, `RejectionReasonCode`). The types
// in `case.ts` give compile-time types; the constants below give
// runtime values for use in Zod enum validators, frontend select
// option lists, and audit payload assembly.
/**
 * Pause reason codes for `sla_paused` audit events.
 * See: docs/compliance/audit-event-taxonomy.md §3.2
 */
export const PauseReasonCode = {
    AWAITING_SUBJECT: 'awaiting_subject',
    AWAITING_VERIFICATION: 'awaiting_verification',
    THIRD_PARTY_DEPENDENCY: 'third_party_dependency',
    OTHER: 'other',
};
/**
 * Rejection reason codes for `case_rejected` audit events.
 * See: docs/compliance/audit-event-taxonomy.md §3.1
 */
export const RejectionReasonCodeEnum = {
    IDENTITY_NOT_VERIFIED: 'identity_not_verified',
    MANIFESTLY_UNFOUNDED: 'manifestly_unfounded',
    REPETITIVE: 'repetitive',
    EXEMPT: 'exempt',
    OUT_OF_SCOPE: 'out_of_scope',
    DUPLICATE: 'duplicate',
};
//# sourceMappingURL=audit.js.map